import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Loader } from 'lucide-react';
import { useSnackbar } from '../../contexts/SnackbarContext';
import api from '../../utils/api';
import './AddRestaurant.scss';

const EditRestaurant = () => {
    const { restaurantID } = useParams();
    const navigate = useNavigate();
    const { success, error } = useSnackbar();
    const [formData, setFormData] = useState({
        name: '',
        district: '',
        state: 'Kerala',
        dish: '',
        googleMapsLink: '',
        location: {
            latitude: '',
            longitude: ''
        },
        description: '',
        keywords: [],
        status: 'available',
        verified: true,
        premium: false,
        contact: {
            phone: '',
            email: '',
            website: ''
        },
        operatingHours: {
            start: '',
            end: '',
            days: []
        },
        paymentMethods: [],
        capacity: '',
        googlePlaceId: '',
        googleRating: '',
        googleReviewCount: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [extractingCoords, setExtractingCoords] = useState(false);

    const dishes = ['Biriyani', 'Neychoru', 'Pathiri', 'Snacks', 'Jeeraka Kanji ജീരക കഞ്ഞി', 'Other'];
    const districts = [
        'Alappuzha', 'Ernakulam', 'Idukki', 'Kannur', 'Kasaragod',
        'Kollam', 'Kottayam', 'Kozhikode', 'Malappuram', 'Palakkad',
        'Pathanamthitta', 'Thiruvananthapuram', 'Thrissur', 'Wayanad'
    ];
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const paymentOptions = ['Cash', 'Card', 'UPI', 'Net Banking', 'Wallet'];

    useEffect(() => {
        fetchRestaurant();
    }, [restaurantID]);

    const fetchRestaurant = async () => {
        try {
            const response = await api.get(`/admin/restaurants/${restaurantID}`);
            if (response.data.success) {
                const restaurant = response.data.restaurant;
                setFormData({
                    name: restaurant.name || '',
                    district: restaurant.district || '',
                    state: restaurant.state || 'Kerala',
                    dish: restaurant.dish || '',
                    googleMapsLink: restaurant.googleMapsLink || '',
                    location: restaurant.location || { latitude: '', longitude: '' },
                    description: restaurant.description || '',
                    keywords: restaurant.keywords || [],
                    status: restaurant.status || 'available',
                    verified: restaurant.verified !== undefined ? restaurant.verified : true,
                    premium: restaurant.premium || false,
                    contact: restaurant.contact || { phone: '', email: '', website: '' },
                    operatingHours: restaurant.operatingHours || { start: '', end: '', days: [] },
                    paymentMethods: restaurant.paymentMethods || [],
                    capacity: restaurant.capacity || '',
                    googlePlaceId: restaurant.googlePlaceId || '',
                    googleRating: restaurant.googleRating || '',
                    googleReviewCount: restaurant.googleReviewCount || '',
                    namazFacility: restaurant.namazFacility || false,
                    womenFacility: restaurant.womenFacility || false
                });
            }
        } catch (err) {
            error('Error loading restaurant');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'googleMapsLink') {
            setFormData(prev => ({
                ...prev,
                googleMapsLink: value
            }));
        } else if (name.includes('location.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                location: {
                    ...prev.location,
                    [field]: value
                }
            }));
        } else if (name.includes('contact.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                contact: {
                    ...prev.contact,
                    [field]: value
                }
            }));
        } else if (name.includes('operatingHours.')) {
            const field = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                operatingHours: {
                    ...prev.operatingHours,
                    [field]: value
                }
            }));
        } else if (name === 'keywords') {
            setFormData(prev => ({
                ...prev,
                keywords: value.split(',').map(k => k.trim()).filter(k => k)
            }));
        } else if (name === 'paymentMethod') {
            setFormData(prev => ({
                ...prev,
                paymentMethods: checked
                    ? [...prev.paymentMethods, value]
                    : prev.paymentMethods.filter(m => m !== value)
            }));
        } else if (name === 'operatingDay') {
            setFormData(prev => ({
                ...prev,
                operatingHours: {
                    ...prev.operatingHours,
                    days: checked
                        ? [...prev.operatingHours.days, value]
                        : prev.operatingHours.days.filter(d => d !== value)
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : (type === 'number' && value !== '' ? parseFloat(value) : value)
            }));
        }
    };

    const handleExtractCoordinates = async () => {
        const link = formData.googleMapsLink?.trim();
        
        if (!link || link === '') {
            error('Please enter a Google Maps link first');
            return;
        }

        if (!link.startsWith('http://') && !link.startsWith('https://')) {
            error('Please enter a valid URL starting with http:// or https://');
            return;
        }

        setExtractingCoords(true);
        try {
            const response = await api.post('/admin/restaurants/extract-coordinates', {
                restaurantID,
                googleMapsLink: link
            });
            
            if (response.data.success && response.data.coordinates) {
                setFormData(prev => ({
                    ...prev,
                    location: {
                        latitude: response.data.coordinates.latitude,
                        longitude: response.data.coordinates.longitude
                    }
                }));
                success(`Coordinates extracted: ${response.data.coordinates.latitude.toFixed(6)}, ${response.data.coordinates.longitude.toFixed(6)}`);
            } else {
                error('Could not extract coordinates from the URL. Please check the link format.');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Error extracting coordinates';
            error(errorMessage);
        } finally {
            setExtractingCoords(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const submitData = {
                ...formData,
                capacity: formData.capacity ? parseInt(formData.capacity) : null,
                googleRating: formData.googleRating ? parseFloat(formData.googleRating) : null,
                googleReviewCount: formData.googleReviewCount ? parseInt(formData.googleReviewCount) : 0
            };
            const response = await api.put(`/admin/restaurants/${restaurantID}`, submitData);
            if (response.data.success) {
                success('Restaurant updated successfully!');
                navigate(`/restaurants/${restaurantID}`);
            }
        } catch (err) {
            error(err.response?.data?.message || 'Error updating restaurant');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="loading">Loading restaurant...</div>;
    }

    return (
        <div className="add-restaurant">
            <div className="page-header">
                <button className="back-button" onClick={() => navigate(`/restaurants/${restaurantID}`)}>
                    <ArrowLeft size={20} />
                    Back
                </button>
                <h2>Edit Restaurant</h2>
            </div>

            <form className="restaurant-form" onSubmit={handleSubmit}>
                <div className="form-section">
                    <h3>Basic Information</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Restaurant Name *</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>District *</label>
                            <select
                                name="district"
                                value={formData.district}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select District</option>
                                {districts.map(d => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>State *</label>
                            <input
                                type="text"
                                name="state"
                                value={formData.state}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Food Type *</label>
                            <select
                                name="dish"
                                value={formData.dish}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select Food Type</option>
                                {dishes.map(d => (
                                    <option key={d} value={d}>{d}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Google Maps Link *</label>
                        <div className="input-with-button">
                            <input
                                type="url"
                                name="googleMapsLink"
                                value={formData.googleMapsLink}
                                onChange={handleChange}
                                required
                                placeholder="https://www.google.com/maps/... or https://g.co/kgs/..."
                            />
                            <button
                                type="button"
                                className="extract-coords-btn"
                                onClick={handleExtractCoordinates}
                                disabled={extractingCoords || !formData.googleMapsLink}
                                title="Extract coordinates from URL"
                            >
                                {extractingCoords ? (
                                    <Loader size={16} className="spinning" />
                                ) : (
                                    <>
                                        <MapPin size={16} />
                                        Extract
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Latitude</label>
                            <input
                                type="number"
                                name="location.latitude"
                                value={formData.location.latitude}
                                onChange={handleChange}
                                step="any"
                            />
                        </div>

                        <div className="form-group">
                            <label>Longitude</label>
                            <input
                                type="number"
                                name="location.longitude"
                                value={formData.location.longitude}
                                onChange={handleChange}
                                step="any"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Description</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            rows="4"
                        />
                    </div>

                    <div className="form-group">
                        <label>Keywords (comma-separated)</label>
                        <input
                            type="text"
                            name="keywords"
                            value={Array.isArray(formData.keywords) ? formData.keywords.join(', ') : ''}
                            onChange={handleChange}
                            placeholder="keyword1, keyword2, keyword3"
                        />
                    </div>
                </div>

                <div className="form-section">
                    <h3>Contact Information</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Phone</label>
                            <input
                                type="tel"
                                name="contact.phone"
                                value={formData.contact.phone}
                                onChange={handleChange}
                                placeholder="+91 1234567890"
                            />
                        </div>

                        <div className="form-group">
                            <label>Email</label>
                            <input
                                type="email"
                                name="contact.email"
                                value={formData.contact.email}
                                onChange={handleChange}
                                placeholder="restaurant@example.com"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Website</label>
                        <input
                            type="url"
                            name="contact.website"
                            value={formData.contact.website}
                            onChange={handleChange}
                            placeholder="https://www.example.com"
                        />
                    </div>
                </div>

                <div className="form-section">
                    <h3>Operating Hours</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Start Time</label>
                            <input
                                type="time"
                                name="operatingHours.start"
                                value={formData.operatingHours.start}
                                onChange={handleChange}
                            />
                        </div>

                        <div className="form-group">
                            <label>End Time</label>
                            <input
                                type="time"
                                name="operatingHours.end"
                                value={formData.operatingHours.end}
                                onChange={handleChange}
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Operating Days</label>
                        <div className="checkbox-group">
                            {daysOfWeek.map(day => (
                                <label key={day} className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="operatingDay"
                                        value={day}
                                        checked={formData.operatingHours.days.includes(day)}
                                        onChange={handleChange}
                                    />
                                    {day}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Additional Information</h3>
                    <div className="form-group">
                        <label>Payment Methods</label>
                        <div className="checkbox-group">
                            {paymentOptions.map(method => (
                                <label key={method} className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="paymentMethod"
                                        value={method}
                                        checked={formData.paymentMethods.includes(method)}
                                        onChange={handleChange}
                                    />
                                    {method}
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Capacity (seating)</label>
                            <input
                                type="number"
                                name="capacity"
                                value={formData.capacity}
                                onChange={handleChange}
                                min="1"
                                placeholder="50"
                            />
                        </div>

                        <div className="form-group">
                            <label>Google Place ID</label>
                            <input
                                type="text"
                                name="googlePlaceId"
                                value={formData.googlePlaceId}
                                onChange={handleChange}
                                placeholder="ChIJ..."
                            />
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Google Rating (0-5)</label>
                            <input
                                type="number"
                                name="googleRating"
                                value={formData.googleRating}
                                onChange={handleChange}
                                min="0"
                                max="5"
                                step="0.1"
                                placeholder="4.5"
                            />
                        </div>

                        <div className="form-group">
                            <label>Google Review Count</label>
                            <input
                                type="number"
                                name="googleReviewCount"
                                value={formData.googleReviewCount}
                                onChange={handleChange}
                                min="0"
                                placeholder="100"
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3>Status & Settings</h3>
                    <div className="form-row">
                        <div className="form-group">
                            <label>Status</label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                            >
                                <option value="available">Available</option>
                                <option value="unavailable">Unavailable</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>
                                <input
                                    type="checkbox"
                                    name="verified"
                                    checked={formData.verified}
                                    onChange={handleChange}
                                />
                                Verified
                            </label>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                name="premium"
                                checked={formData.premium}
                                onChange={handleChange}
                            />
                            Premium Badge
                        </label>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>
                                <input
                                    type="checkbox"
                                    name="namazFacility"
                                    checked={formData.namazFacility}
                                    onChange={handleChange}
                                />
                                Namaz Facility Available
                            </label>
                            <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                                Restaurant has prayer facility for customers
                            </small>
                        </div>

                        <div className="form-group">
                            <label>
                                <input
                                    type="checkbox"
                                    name="womenFacility"
                                    checked={formData.womenFacility}
                                    onChange={handleChange}
                                />
                                Facility for Women
                            </label>
                            <small style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                                Prayer area available for women
                            </small>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate(`/restaurants/${restaurantID}`)}>
                        Cancel
                    </button>
                    <button type="submit" disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditRestaurant;
